import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAppData } from './AppDataContext';
import { getContrastForeground, hexToHsl } from '@/lib/colorUtils';

interface ThemeColors {
  header: string;
  menu: string;
  primary: string;
  secondary: string;
  button: string;
  title: string;
  text: string;
}

interface ThemeContextType {
  colors: ThemeColors;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { config } = useAppData();
  const [colors, setColors] = useState<ThemeColors>({
    header: '#FFFFFF',
    menu: '#333333',
    primary: '#333333',
    secondary: '#666666',
    button: '#333333',
    title: '#000000',
    text: '#333333'
  });

  useEffect(() => {
    if (config?.themeColors) {
      const normalizeColor = (color: string) => {
        if (!color) return '';
        let normalized = color;
        if (!normalized.startsWith('#')) {
          normalized = '#' + normalized;
        }
        if (normalized.length === 9 && normalized.endsWith('FF')) {
          normalized = normalized.slice(0, 7);
        }
        return normalized;
      };

      setColors({
        header: normalizeColor(config.themeColors.header) || '#FFFFFF',
        menu: normalizeColor(config.themeColors.menu) || '#333333',
        primary: normalizeColor(config.themeColors.primary) || '#333333',
        secondary: normalizeColor(config.themeColors.secondary) || '#666666',
        button: normalizeColor(config.themeColors.button) || '#333333',
        title: normalizeColor(config.themeColors.title) || '#000000',
        text: normalizeColor(config.themeColors.text) || '#333333'
      });
    }
  }, [config]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary', hexToHsl(colors.primary));
    root.style.setProperty('--primary-foreground', getContrastForeground(colors.primary));
    root.style.setProperty('--secondary', hexToHsl(colors.secondary));
    root.style.setProperty('--ring', hexToHsl(colors.primary));
    root.style.setProperty('--full-banner-blur-tint', '#000000');
  }, [colors]);

  return (
    <ThemeContext.Provider value={{ colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

